"use client"

import { Quote, Star } from "lucide-react"
import { Avatar, AvatarFallback } from "./ui/avatar"
import { Card, CardContent } from "./ui/card"
import { Testimonial } from "@/types/types"
import { format, parseISO } from "date-fns"
import { useState } from "react"
import { cn } from "@/lib/utils"

export const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
        <>
            {/* Full Stars */}
            {Array.from({ length: fullStars }).map((_, index) => (
                <Star
                    key={`full-${index}`}
                    size={16}
                    className="fill-yellow-400 text-yellow-400"
                />
            ))}

            {/* Half Star */}
            {hasHalfStar && (
                <div className="relative inline-block" key="half" style={{ width: '16px', height: '16px' }}>
                    {/* Empty star background */}
                    <Star
                        size={16}
                        className="absolute fill-gray-200 text-gray-200 dark:fill-gray-700 dark:text-gray-700"
                    />
                    {/* Half filled star overlay with clip */}
                    <div className="absolute inset-0" style={{ clipPath: 'inset(0 50% 0 0)' }}>
                        <Star
                            size={16}
                            className="fill-yellow-400 text-yellow-400"
                        />
                    </div>
                </div>
            )}

            {/* Empty Stars */}
            {Array.from({ length: emptyStars }).map((_, index) => (
                <Star
                    key={`empty-${index}`}
                    size={16}
                    className="fill-gray-200 text-gray-200 dark:fill-gray-700 dark:text-gray-700"
                />
            ))}
        </>
    );
};

type TestimonialCardProps = {
    testimonial: Testimonial
}

const TestimonialCard = ({ testimonial }: TestimonialCardProps) => {
    const [isExpanded, setIsExpanded] = useState(false)

    return (
        <Card className="backdrop-blur-3xl bg-gradient-gray h-full border-border/50 hover:border-primary/30 transition-colors">
            <CardContent className="p-6 flex flex-col h-full">
                {/* Quote Icon */}
                <div className="mb-4">
                    <Quote className="size-8 text-primary/60" />
                </div>

                {/* Rating */}
                <div className="flex gap-1 mb-4">
                    {renderStars(testimonial.rate)}
                </div>

                {/* Feedback Text */}
                <div className="mb-6 flex-grow">
                    <p
                        className={cn(
                            "text-foreground/90 leading-relaxed",
                            !isExpanded && "line-clamp-1"
                        )}
                    >
                        &ldquo;{testimonial.feedback}&rdquo;
                    </p>
                    {testimonial.feedback.length > 45 &&
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="inline mt-2 text-xs text-gray-500 cursor-pointer hover:text-primary hover:underline font-medium transition-colors"
                        >
                            {isExpanded ? "Show less" : "Show more"}
                        </button>
                    }
                </div>

                {/* Author Info */}
                <div className="flex items-center justify-between pt-4 border-t border-border/50">
                    <div className="flex items-center gap-3">
                        <Avatar className="size-10">
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                                {(() => {
                                    const initials = testimonial.name
                                        .split(" ")
                                        .map((n) => n[0])
                                        .join("")
                                        .toUpperCase()
                                        .slice(0, 2);
                                    return initials;
                                })()}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                            <p className="font-semibold text-sm">{testimonial.name}</p>
                            {testimonial.date && (
                                <p className="text-xs text-muted-foreground">
                                    {format(parseISO(testimonial.date), "dd MMM yyyy")}
                                </p>
                            )}
                        </div>
                    </div>
                    {testimonial.source && (
                        <div className="px-2 py-1 text-xs font-medium text-primary/80 bg-primary/10 rounded-md border border-primary/20">
                            {testimonial.source}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}

export default TestimonialCard

